import React, { useState } from 'react';
import { Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';
import { signInWithEmail, signUpWithEmail, resetPassword, signInWithGoogle } from '../firebase/auth';

const UserRegistrationModal = ({ isOpen, onClose, onSuccess, currentUserId }) => {
  const [activeTab, setActiveTab] = useState('signin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const user = await signInWithEmail(email, password);
      
      // Register user with backend and link files
      await registerUserWithBackend(user, currentUserId);
      
      setSuccess('Successfully signed in! Your files have been linked to your account.');
      setTimeout(() => {
        onSuccess(user);
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Sign in error:', error);
      setError(error.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const user = await signUpWithEmail(email, password, displayName);
      
      // Register user with backend and link files
      await registerUserWithBackend(user, currentUserId);
      
      setSuccess('Account created successfully! Your files have been linked to your account.');
      setTimeout(() => {
        onSuccess(user);
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Sign up error:', error);
      setError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const user = await signInWithGoogle();
      
      // Register user with backend and link files
      await registerUserWithBackend(user, currentUserId);
      
      setSuccess('Successfully signed in with Google! Your files have been linked to your account.');
      setTimeout(() => {
        onSuccess(user);
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Google sign in error:', error);
      setError(error.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await resetPassword(email);
      setSuccess('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const registerUserWithBackend = async (firebaseUser, anonymousUserId) => {
    try {
      console.log('🔐 Registering user with backend:', { 
        email: firebaseUser.email, 
        firebaseUid: firebaseUser.uid,
        anonymousUserId 
      });

      const apiBase = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';
      
      const response = await fetch(`${apiBase}/api/user/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: firebaseUser.email,
          firebaseUid: firebaseUser.uid,
          displayName: firebaseUser.displayName || displayName,
          anonymousUserId: anonymousUserId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Backend registration failed:', response.status, errorData);
        throw new Error(`Failed to register user with backend: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      console.log('User registered successfully:', result);
      
      // Update the user ID in localStorage and cookie to the new authenticated user ID
      if (result.userId) {
        console.log(`🔄 Updating user ID from ${anonymousUserId} to ${result.userId}`);
        localStorage.setItem('fileshare_user_id', result.userId);
        const exp = new Date();
        exp.setFullYear(exp.getFullYear() + 1);
        document.cookie = `fileshare_user_id=${encodeURIComponent(result.userId)}; expires=${exp.toUTCString()}; path=/; SameSite=Lax`;
        
        // Also update the global auth context
        if (typeof window !== 'undefined' && window.__authContext) {
          window.__authContext.user = firebaseUser;
        }
      }
      
      return result;
    } catch (error) {
      console.error('Backend registration error:', error);
      throw error;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div style={{ backgroundColor: '#242938', border: '1px solid #374151', borderRadius: '3px', padding: '18px', width: '100%', maxWidth: '360px' }}>
        <div className="text-center mb-3">
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '4px' }}>Create Account</h2>
          <p style={{ fontSize: '12px', color: '#9ca3af' }}>
            Link your files to an account for premium features
          </p>
        </div>
        
        <div>
          {/* Tab Navigation */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px', backgroundColor: '#374151', padding: '2px', borderRadius: '3px', marginBottom: '14px' }}>
            <button 
              onClick={() => setActiveTab('signin')}
              style={{ 
                padding: '6px 8px', 
                borderRadius: '3px', 
                border: 'none',
                backgroundColor: activeTab === 'signin' ? '#3b82f6' : 'transparent',
                color: activeTab === 'signin' ? 'white' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Sign In
            </button>
            <button 
              onClick={() => setActiveTab('signup')}
              style={{ 
                padding: '6px 8px', 
                borderRadius: '3px', 
                border: 'none',
                backgroundColor: activeTab === 'signup' ? '#3b82f6' : 'transparent',
                color: activeTab === 'signup' ? 'white' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Sign Up
            </button>
            <button 
              onClick={() => setActiveTab('reset')}
              style={{ 
                padding: '6px 8px', 
                borderRadius: '3px', 
                border: 'none',
                backgroundColor: activeTab === 'reset' ? '#3b82f6' : 'transparent',
                color: activeTab === 'reset' ? 'white' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Reset
            </button>
          </div>

          {/* Sign In Tab */}
          {activeTab === 'signin' && (
            <form onSubmit={handleSignIn} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '4px' }}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail style={{ position: 'absolute', left: '8px', top: '8px', height: '14px', width: '14px', color: '#9ca3af' }} />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px 8px 8px 30px', 
                      backgroundColor: '#374151', 
                      border: '1px solid #4b5563', 
                      borderRadius: '3px', 
                      color: '#e2e8f0',
                      fontSize: '13px'
                    }}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '4px' }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: '8px', top: '8px', height: '14px', width: '14px', color: '#9ca3af' }} />
                  <input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px 8px 8px 30px', 
                      backgroundColor: '#374151', 
                      border: '1px solid #4b5563', 
                      borderRadius: '3px', 
                      color: '#e2e8f0',
                      fontSize: '13px'
                    }}
                    required
                  />
                </div>
              </div>

              {error && (
                <div style={{ backgroundColor: '#dc2626', color: 'white', padding: '8px', borderRadius: '3px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertCircle style={{ height: '14px', width: '14px' }} />
                  {error}
                </div>
              )}

              {success && (
                <div style={{ backgroundColor: '#059669', color: 'white', padding: '8px', borderRadius: '3px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle style={{ height: '14px', width: '14px' }} />
                  {success}
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                style={{ 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  padding: '8px 12px', 
                  borderRadius: '3px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          )}

          {/* Sign Up Tab */}
          {activeTab === 'signup' && (
            <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '4px' }}>Name</label>
                <div style={{ position: 'relative' }}>
                  <User style={{ position: 'absolute', left: '8px', top: '8px', height: '14px', width: '14px', color: '#9ca3af' }} />
                  <input
                    type="text"
                    placeholder="Display Name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px 8px 8px 30px', 
                      backgroundColor: '#374151', 
                      border: '1px solid #4b5563', 
                      borderRadius: '3px', 
                      color: '#e2e8f0',
                      fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '4px' }}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail style={{ position: 'absolute', left: '8px', top: '8px', height: '14px', width: '14px', color: '#9ca3af' }} />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px 8px 8px 30px', 
                      backgroundColor: '#374151', 
                      border: '1px solid #4b5563', 
                      borderRadius: '3px', 
                      color: '#e2e8f0',
                      fontSize: '13px'
                    }}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '4px' }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: '8px', top: '8px', height: '14px', width: '14px', color: '#9ca3af' }} />
                  <input
                    type="password"
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px 8px 8px 30px', 
                      backgroundColor: '#374151', 
                      border: '1px solid #4b5563', 
                      borderRadius: '3px', 
                      color: '#e2e8f0',
                      fontSize: '13px'
                    }}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '4px' }}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', left: '8px', top: '8px', height: '14px', width: '14px', color: '#9ca3af' }} />
                  <input
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px 8px 8px 30px', 
                      backgroundColor: '#374151', 
                      border: '1px solid #4b5563', 
                      borderRadius: '3px', 
                      color: '#e2e8f0',
                      fontSize: '13px'
                    }}
                    required
                  />
                </div>
              </div>

              {error && (
                <div style={{ backgroundColor: '#dc2626', color: 'white', padding: '8px', borderRadius: '3px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertCircle style={{ height: '14px', width: '14px' }} />
                  {error}
                </div>
              )}

              {success && (
                <div style={{ backgroundColor: '#059669', color: 'white', padding: '8px', borderRadius: '3px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle style={{ height: '14px', width: '14px' }} />
                  {success}
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                style={{ 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  padding: '8px 12px', 
                  borderRadius: '3px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Reset Password Tab */}
          {activeTab === 'reset' && (
            <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#e2e8f0', marginBottom: '4px' }}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail style={{ position: 'absolute', left: '8px', top: '8px', height: '14px', width: '14px', color: '#9ca3af' }} />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '8px 8px 8px 30px', 
                      backgroundColor: '#374151', 
                      border: '1px solid #4b5563', 
                      borderRadius: '3px', 
                      color: '#e2e8f0',
                      fontSize: '13px'
                    }}
                    required
                  />
                </div>
              </div>

              {error && (
                <div style={{ backgroundColor: '#dc2626', color: 'white', padding: '8px', borderRadius: '3px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AlertCircle style={{ height: '14px', width: '14px' }} />
                  {error}
                </div>
              )}

              {success && (
                <div style={{ backgroundColor: '#059669', color: 'white', padding: '8px', borderRadius: '3px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle style={{ height: '14px', width: '14px' }} />
                  {success}
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                style={{ 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  padding: '8px 12px', 
                  borderRadius: '3px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>
            </form>
          )}

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', margin: '12px 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#374151' }}></div>
            <span style={{ padding: '0 8px', fontSize: '11px', color: '#9ca3af' }}>or</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#374151' }}></div>
          </div>

          {/* Google Sign In Button - Now at Bottom */}
          <button 
            onClick={handleGoogleSignIn}
            disabled={loading}
            style={{ 
              backgroundColor: '#ffffff', 
              color: '#374151', 
              padding: '8px 12px', 
              borderRadius: '8px',
              border: '2px solid transparent',
              background: 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, #ff00ff, #ff0000, #ff8800, #ffaa00) border-box',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              width: '100%',
              marginBottom: '12px',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 1px 2px rgba(0, 0, 0, 0.1)'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div style={{ textAlign: 'center' }}>
            <button 
              onClick={handleClose}
              style={{ 
                backgroundColor: 'transparent', 
                color: '#9ca3af', 
                padding: '6px 10px', 
                borderRadius: '3px',
                border: '1px solid #4b5563',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegistrationModal;